import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { leagues } from "@/mocks/leagues";

const navItems = [
  { label: "首页", to: "/" },
  { label: "直播", to: "/live" },
  { label: "回放", to: "/replay" },
  { label: "解析", to: "/analysis" },
  { label: "关于", to: "/about" },
];

export default function Navbar() {
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [leagueOpen, setLeagueOpen] = useState(false);

  const isHome = location.pathname === "/";
  const transparent = isHome && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setLeagueOpen(false);
  }, [location.pathname]);

  const isActive = (to: string) =>
    to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);

  const linkColor = transparent
    ? "text-white/85 hover:text-white"
    : "text-foreground-600 hover:text-primary-600";
  const activeColor = transparent ? "text-white" : "text-primary-600";

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        transparent
          ? "bg-gradient-to-b from-black/55 to-transparent"
          : "bg-background-50/95 backdrop-blur border-b border-background-200"
      }`}
    >
      <nav className="w-full h-16 md:h-[70px] px-4 md:px-8 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2.5 cursor-pointer shrink-0">
          <span className="w-9 h-9 md:w-10 md:h-10 rounded-lg bg-primary-500 flex items-center justify-center text-white">
            <i className="ri-football-fill text-xl" />
          </span>
          <span className="flex flex-col leading-none">
            <span
              className={`font-heading text-lg md:text-xl font-semibold tracking-wide ${
                transparent ? "text-white" : "text-foreground-950"
              }`}
            >
              51<span className="text-accent-500">足球</span>直播
            </span>
            <span
              className={`text-[10px] tracking-wide hidden sm:block ${
                transparent ? "text-white/70" : "text-foreground-400"
              }`}
            >
              高清足球赛事 · 直播与回放
            </span>
          </span>
        </Link>

        <div className="hidden lg:flex items-center gap-1">
          {navItems.slice(0, 2).map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`px-3.5 py-2 text-sm font-medium rounded-md transition-colors cursor-pointer whitespace-nowrap ${
                isActive(item.to) ? activeColor : linkColor
              }`}
            >
              {item.label}
            </Link>
          ))}

          <div
            className="relative"
            onMouseEnter={() => setLeagueOpen(true)}
            onMouseLeave={() => setLeagueOpen(false)}
          >
            <button
              type="button"
              className={`px-3.5 py-2 text-sm font-medium rounded-md transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1 ${
                location.pathname.startsWith("/category")
                  ? activeColor
                  : linkColor
              }`}
            >
              分类
              <i className="ri-arrow-down-s-line text-base" />
            </button>
            {leagueOpen && (
              <div className="absolute top-full left-0 pt-2 w-56">
                <div className="bg-background-50 border border-background-200 rounded-lg p-2 grid grid-cols-2 gap-1">
                  {leagues.map((lg) => (
                    <Link
                      key={lg.key}
                      to={`/category/${lg.key}`}
                      className="px-3 py-2 rounded-md text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer flex items-center justify-between"
                    >
                      {lg.name}
                      <span className="text-[10px] text-foreground-300">{lg.region}</span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          {navItems.slice(2).map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`px-3.5 py-2 text-sm font-medium rounded-md transition-colors cursor-pointer whitespace-nowrap ${
                isActive(item.to) ? activeColor : linkColor
              }`}
            >
              {item.label}
            </Link>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <Link
            to="/search"
            className={`w-9 h-9 flex items-center justify-center rounded-md transition-colors cursor-pointer ${
              transparent
                ? "text-white/85 hover:bg-white/15"
                : "text-foreground-600 hover:bg-background-100"
            }`}
            aria-label="搜索"
          >
            <i className="ri-search-line text-lg" />
          </Link>
          <Link
            to="/live"
            className="hidden sm:inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-accent-500 hover:bg-accent-600 text-white text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse-live" />
            观看直播
          </Link>
          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            className={`lg:hidden w-9 h-9 flex items-center justify-center rounded-md transition-colors cursor-pointer ${
              transparent
                ? "text-white hover:bg-white/15"
                : "text-foreground-700 hover:bg-background-100"
            }`}
            aria-label="菜单"
          >
            <i className={mobileOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"} />
          </button>
        </div>
      </nav>

      {mobileOpen && (
        <div className="lg:hidden bg-background-50 border-b border-background-200 max-h-[calc(100vh-4rem)] overflow-y-auto">
          <div className="px-4 py-3 flex flex-col gap-1">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`px-3 py-2.5 rounded-md text-sm font-medium transition-colors cursor-pointer ${
                  isActive(item.to)
                    ? "bg-primary-50 text-primary-700"
                    : "text-foreground-700 hover:bg-background-100"
                }`}
              >
                {item.label}
              </Link>
            ))}
            <div className="pt-2 mt-1 border-t border-background-100">
              <p className="px-3 py-2 text-xs text-foreground-400">赛事分类</p>
              <div className="grid grid-cols-2 gap-1">
                {leagues.map((lg) => (
                  <Link
                    key={lg.key}
                    to={`/category/${lg.key}`}
                    className="px-3 py-2 rounded-md text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer"
                  >
                    {lg.name}
                  </Link>
                ))}
              </div>
            </div>
            <Link
              to="/search"
              className="mt-2 px-3 py-2.5 rounded-md text-sm font-medium bg-background-100 text-foreground-800 flex items-center gap-2 cursor-pointer"
            >
              <i className="ri-search-line" />
              搜索赛事 / 球队 / 回放
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}